import type { SelectedSalesItemsCar } from 'noddi-types';
import { compareLicensePlates } from 'noddi-util';
import { useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import {
  applyIncomingTireSeason,
  applyTireHotelPickupDecision,
  getNewSalesItemsInState,
  removeMultipleSalesItemsConstructor,
  removeSalesItemConstructor
} from '../helpers/salesItem';
import { BookingStoreContext } from './bookingStoreContext';
import type { BookingState, BookingStoreInitProps } from './bookingStoreStateType';
import { initialStateValues } from './bookingStoreStateType';

const indexOfMatchingSelectedSalesItem = (
  selectedSalesItemsCars: SelectedSalesItemsCar[],
  licensePlate: SelectedSalesItemsCar['licensePlate'],
  salesItemId: SelectedSalesItemsCar['salesItemId']
): number =>
  selectedSalesItemsCars.findIndex(
    (item) => compareLicensePlates(item.licensePlate, licensePlate) && item.salesItemId === salesItemId
  );

// Any change to the selected cars or sales items invalidates the chosen time window.
const timeWindowReset = { selectedTimeWindow: undefined };

// NB, keep stuff in multiple files to avoid HMR issues with zustand stores..

/**
 * Creates a booking store instance with a configurable storage name
 * @param initProps - Initialization props containing the store name
 * @returns A Zustand store instance
 */
export const createBookingStore = (initProps: BookingStoreInitProps) => {
  return createStore<BookingState>()(
    persist(
      devtools((set) => ({
        ...initialStateValues,
        clearStore: () => set(initialStateValues),

        setSelectedAddress: (selectedAddress) =>
          set({
            selectedAddress,
            selectedSalesItemsCars: [],
            ...timeWindowReset
          }),

        setSelectedSalesItemsCars: (selectedSalesItemsCars) => set({ selectedSalesItemsCars }),

        /** Customer's answer to the backend tire hotel location suggestion (wheel_pickup_location_suggestion). */
        setTireHotelPickupDecision: (decision) => {
          set((state) => ({
            selectedSalesItemsCars: applyTireHotelPickupDecision(state.selectedSalesItemsCars, decision)
          }));
        },

        setIncomingTireSeason: (params) =>
          set((state) => ({ selectedSalesItemsCars: applyIncomingTireSeason(state.selectedSalesItemsCars, params) })),

        addCar: (carToAdd) => {
          set((state) => {
            const selectedCars = state.selectedCars;
            const isAlreadySelected = selectedCars.some((selectedCar) =>
              compareLicensePlates(selectedCar.licensePlate, carToAdd.licensePlate)
            );

            if (isAlreadySelected) {
              return state;
            }

            return {
              selectedCars: [...selectedCars, carToAdd],
              ...timeWindowReset
            };
          });
        },
        removeCar: (carToRemove) => {
          set((state) => ({
            selectedCars: state.selectedCars.filter(
              (selectedCar) => !compareLicensePlates(selectedCar.licensePlate, carToRemove.licensePlate)
            ),
            selectedSalesItemsCars: state.selectedSalesItemsCars.filter(
              (selectedSalesItemsCar) =>
                !compareLicensePlates(selectedSalesItemsCar.licensePlate, carToRemove.licensePlate)
            )
          }));
        },

        addSalesItem: (params) => {
          set((state) => {
            const selectedSalesItemsCars = state.selectedSalesItemsCars;
            const { mutuallyExclusiveSalesItemIds, ...salesItemToAdd } = params;

            const existingIdx = indexOfMatchingSelectedSalesItem(
              selectedSalesItemsCars,
              salesItemToAdd.licensePlate,
              salesItemToAdd.salesItemId
            );

            if (existingIdx !== -1) {
              const next = [...selectedSalesItemsCars];
              const existing = next[existingIdx]!;
              next[existingIdx] = { ...existing, ...salesItemToAdd };
              return {
                selectedSalesItemsCars: next,
                ...timeWindowReset
              };
            }

            const newItems = getNewSalesItemsInState({
              selectedSalesItemsCars,
              salesItemToAdd,
              mutuallyExclusiveSalesItemIds
            });
            return {
              selectedSalesItemsCars: newItems,
              ...timeWindowReset
            };
          });
        },

        addSalesItems: (items) => {
          set((state) => {
            const updatedSalesItemsCars = items.reduce<SelectedSalesItemsCar[]>((acc, currentItem) => {
              const { mutuallyExclusiveSalesItemIds, ...currentSalesItemToAdd } = currentItem;
              const existingIdx = indexOfMatchingSelectedSalesItem(
                acc,
                currentSalesItemToAdd.licensePlate,
                currentSalesItemToAdd.salesItemId
              );

              if (existingIdx !== -1) {
                return acc.map((item, index) => (index === existingIdx ? { ...item, ...currentSalesItemToAdd } : item));
              }

              // make sure to remove all trailing addons if a primary item is added
              return getNewSalesItemsInState({
                selectedSalesItemsCars: acc,
                salesItemToAdd: currentSalesItemToAdd,
                mutuallyExclusiveSalesItemIds
              });
            }, state.selectedSalesItemsCars);

            return {
              selectedSalesItemsCars: updatedSalesItemsCars,
              ...timeWindowReset
            };
          });
        },

        removeSalesItem: (itemToRemove) => {
          set((state) =>
            removeSalesItemConstructor({
              selectedSalesItemsCars: state.selectedSalesItemsCars,
              itemToRemove
            })
          );
        },
        removeSalesItems: (itemsToRemove) => {
          set((state) =>
            removeMultipleSalesItemsConstructor({
              selectedSalesItemsCars: state.selectedSalesItemsCars,
              itemsToRemove
            })
          );
        },
        setBookingProposalId: (bookingProposalId) => set({ bookingProposalId }),
        setBookingProposalCustomer: (bookingProposalCustomer) => set({ bookingProposalCustomer }),

        setSelectedTimeWindow: (selectedTimeWindow) => set({ selectedTimeWindow }),
        setComment: (comment) => set({ comment }),
        setInsuranceCompanyForCar: ({ licensePlate, insuranceCompanyName }) => {
          set((state) => {
            const hasExistingEntry = state.requiredBookingInfo.cars.some((car) =>
              compareLicensePlates(car.licensePlate, licensePlate)
            );

            // if company we know that glass damage is covered
            const infoToAdd = { licensePlate, insuranceCompanyName, isGlassDamageCovered: true };
            if (hasExistingEntry) {
              return {
                requiredBookingInfo: {
                  ...state.requiredBookingInfo,
                  cars: state.requiredBookingInfo.cars.map((requiredBookingInfoCar) =>
                    compareLicensePlates(requiredBookingInfoCar.licensePlate, licensePlate)
                      ? { ...requiredBookingInfoCar, ...infoToAdd }
                      : requiredBookingInfoCar
                  )
                }
              };
            } else {
              return {
                requiredBookingInfo: {
                  ...state.requiredBookingInfo,
                  cars: [...state.requiredBookingInfo.cars, infoToAdd]
                }
              };
            }
          });
        },

        setDateOfDamageForCar: ({ licensePlate, dateOfDamage }) => {
          set((state) => {
            const hasExistingEntry = state.requiredBookingInfo.cars.some((car) =>
              compareLicensePlates(car.licensePlate, licensePlate)
            );
            if (hasExistingEntry) {
              return {
                requiredBookingInfo: {
                  ...state.requiredBookingInfo,
                  cars: state.requiredBookingInfo.cars.map((requiredBookingInfoCar) =>
                    compareLicensePlates(requiredBookingInfoCar.licensePlate, licensePlate)
                      ? { ...requiredBookingInfoCar, dateOfDamage }
                      : requiredBookingInfoCar
                  )
                }
              };
            } else {
              return {
                requiredBookingInfo: {
                  ...state.requiredBookingInfo,
                  cars: [...state.requiredBookingInfo.cars, { licensePlate, dateOfDamage }]
                }
              };
            }
          });
        },
        setIsGlassDamageCoveredForCar: ({ licensePlate, isGlassDamageCovered }) => {
          set((state) => {
            const hasExistingEntry = state.requiredBookingInfo.cars.some((car) =>
              compareLicensePlates(car.licensePlate, licensePlate)
            );
            if (hasExistingEntry) {
              return {
                requiredBookingInfo: {
                  ...state.requiredBookingInfo,
                  cars: state.requiredBookingInfo.cars.map((requiredBookingInfoCar) =>
                    compareLicensePlates(requiredBookingInfoCar.licensePlate, licensePlate)
                      ? { ...requiredBookingInfoCar, isGlassDamageCovered }
                      : requiredBookingInfoCar
                  )
                }
              };
            } else {
              return {
                requiredBookingInfo: {
                  ...state.requiredBookingInfo,
                  cars: [...state.requiredBookingInfo.cars, { licensePlate, isGlassDamageCovered }]
                }
              };
            }
          });
        },

        setPinLocation: (pinLocation) => {
          set({ pinLocation });
        }
      })),
      {
        name: initProps.storeName,
        storage: createJSONStorage(() => sessionStorage)
      }
    )
  );
};

function useBookingStoreContext<T>(selector: (state: BookingState) => T): T {
  const store = useContext(BookingStoreContext);
  if (!store) {
    throw new Error('Missing BookingStoreContext.Provider in the tree');
  }
  return useStore(store, selector);
}

// Getters
export const useBookingCars = () => useBookingStoreContext((state) => state.selectedCars);
export const useBookingAddress = () => useBookingStoreContext((state) => state.selectedAddress);
export const useBookingSelectedSalesItemsCars = () => useBookingStoreContext((state) => state.selectedSalesItemsCars);
export const useBookingTimeWindow = () => useBookingStoreContext((state) => state.selectedTimeWindow);
export const useBookingComment = () => useBookingStoreContext((state) => state.comment);
export const useBookingProposalId = () => useBookingStoreContext((state) => state.bookingProposalId);
export const useBookingProposalCustomer = () => useBookingStoreContext((state) => state.bookingProposalCustomer);
export const useBookingCarsInsuranceInfo = () => useBookingStoreContext((state) => state.requiredBookingInfo.cars);
export const useBookingPinLocation = () => useBookingStoreContext((state) => state.pinLocation);

// Actions
export const useBookingActions = () => {
  const setSelectedAddress = useBookingStoreContext((state) => state.setSelectedAddress);
  const addCar = useBookingStoreContext((state) => state.addCar);
  const removeCar = useBookingStoreContext((state) => state.removeCar);
  const clearStore = useBookingStoreContext((state) => state.clearStore);
  const addSalesItem = useBookingStoreContext((state) => state.addSalesItem);
  const removeSalesItem = useBookingStoreContext((state) => state.removeSalesItem);
  const setSelectedTimeWindow = useBookingStoreContext((state) => state.setSelectedTimeWindow);
  const setComment = useBookingStoreContext((state) => state.setComment);
  const addSalesItems = useBookingStoreContext((state) => state.addSalesItems);
  const removeSalesItems = useBookingStoreContext((state) => state.removeSalesItems);
  const setSelectedSalesItemsCars = useBookingStoreContext((state) => state.setSelectedSalesItemsCars);
  const setTireHotelPickupDecision = useBookingStoreContext((state) => state.setTireHotelPickupDecision);
  const setIncomingTireSeason = useBookingStoreContext((state) => state.setIncomingTireSeason);
  const setInsuranceCompanyForCar = useBookingStoreContext((state) => state.setInsuranceCompanyForCar);
  const setDateOfDamageForCar = useBookingStoreContext((state) => state.setDateOfDamageForCar);
  const setIsGlassDamageCoveredForCar = useBookingStoreContext((state) => state.setIsGlassDamageCoveredForCar);
  const setPinLocation = useBookingStoreContext((state) => state.setPinLocation);
  const setBookingProposalId = useBookingStoreContext((state) => state.setBookingProposalId);
  const setBookingProposalCustomer = useBookingStoreContext((state) => state.setBookingProposalCustomer);

  return {
    setSelectedAddress,
    addCar,
    removeCar,
    clearStore,
    addSalesItem,
    removeSalesItem,
    setSelectedTimeWindow,
    setComment,
    addSalesItems,
    removeSalesItems,
    setSelectedSalesItemsCars,
    setTireHotelPickupDecision,
    setIncomingTireSeason,
    setInsuranceCompanyForCar,
    setDateOfDamageForCar,
    setIsGlassDamageCoveredForCar,
    setPinLocation,
    setBookingProposalId,
    setBookingProposalCustomer
  };
};
